import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@massilia/ui';
import { IconInput } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { FieldError } from './field-error';
export default function NumberField({ containerClassName, asString, decimal, optional, ...props }) {
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    // Raw string state to support intermediate decimal input like "70." or "70,"
    const [rawValue, setRawValue] = useState(() => {
        if (field.state.value === null || field.state.value === 0)
            return '';
        return String(field.state.value);
    });
    // Sync rawValue when field value changes externally (e.g. form reset, initial values)
    useEffect(() => {
        const fieldVal = field.state.value;
        if (fieldVal === null || fieldVal === 0) {
            setRawValue((prev) => {
                // Don't reset if the user is actively typing a decimal
                if (decimal && prev !== '' && (prev.endsWith('.') || prev.endsWith(',')))
                    return prev;
                return '';
            });
        }
        else {
            setRawValue((prev) => {
                // Don't overwrite if the current raw value already parses to the same number
                const normalized = prev.replace(',', '.');
                if (Number(normalized) === fieldVal)
                    return prev;
                return String(fieldVal);
            });
        }
    }, [field.state.value, decimal]);
    if (decimal) {
        return (_jsxs("div", { className: cn('flex flex-1 flex-col gap-1', containerClassName), children: [_jsx(IconInput, { "aria-invalid": errors.length > 0, buttonRightCallBack: optional
                        ? () => {
                            field.handleChange(null);
                            setRawValue('');
                        }
                        : undefined, buttonRightIcon: optional ? X : undefined, inputMode: "decimal", onBlur: () => {
                        field.handleBlur();
                        // On blur, clean up trailing separators
                        const normalized = rawValue.replace(',', '.');
                        const num = Number(normalized);
                        if (!Number.isNaN(num) && rawValue !== '') {
                            setRawValue(String(num));
                        }
                    }, onChange: (e) => {
                        const input = e.target.value;
                        // Allow empty input
                        if (input === '') {
                            setRawValue('');
                            if (optional) {
                                field.handleChange(null);
                            }
                            else {
                                field.handleChange(0);
                            }
                            return;
                        }
                        // Allow valid decimal patterns: digits, one dot or comma, more digits
                        // Also allow intermediate states like "70." or "70," or just "."
                        const decimalPattern = /^-?\d*[.,]?\d*$/;
                        if (!decimalPattern.test(input)) {
                            return; // reject invalid characters
                        }
                        setRawValue(input);
                        // Parse the value (normalize comma to dot)
                        const normalized = input.replace(',', '.');
                        const numericValue = Number(normalized);
                        // Only update form if we have a valid complete number
                        if (!Number.isNaN(numericValue) &&
                            normalized !== '' &&
                            normalized !== '.' &&
                            normalized !== '-') {
                            field.handleChange(numericValue);
                        }
                    }, optional: optional, value: rawValue, ...props }), _jsx(FieldError, { errors: errors })] }));
    }
    return (_jsxs("div", { className: cn('flex flex-1 flex-col gap-1', containerClassName), children: [_jsx(IconInput, { "aria-invalid": errors.length > 0, buttonRightCallBack: optional
                    ? () => {
                        field.handleChange(null);
                    }
                    : undefined, buttonRightIcon: optional ? X : undefined, inputMode: "numeric", onBlur: field.handleBlur, onChange: (e) => {
                    const numericValue = Number(e.target.value);
                    if (Number.isNaN(numericValue) || e.target.value === '') {
                        if (optional) {
                            field.handleChange(null);
                        }
                        else {
                            field.handleChange(0);
                        }
                        return;
                    }
                    field.handleChange(Number(e.target.value));
                }, optional: optional, value: field.state.value === null ? '' : field.state.value === 0 ? '' : field.state.value, ...props }), _jsx(FieldError, { asString: asString, errors: errors })] }));
}
